import { useTranslation } from "react-i18next";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import { colorClass, formatEur, formatPercent } from "../../lib/format-kpi.js";
import type { InvestmentDetail, PeriodReturnDto } from "../../lib/investments";

interface Props {
  periodReturn: PeriodReturnDto;
  kind: InvestmentDetail["kind"];
}

export function PerformanceSection({ periodReturn, kind }: Props) {
  const { t } = useTranslation();
  const [detailsExpanded, setDetailsExpanded] = useLocalStorage<boolean>(
    "finanz-app.investments.detail.expandedView",
    false,
  );

  if (!periodReturn.hasData) {
    return (
      <section className="border rounded p-4">
        <header className="mb-2">
          <h2 className="text-lg font-semibold">{t("investments.period.section_title")}</h2>
        </header>
        <p className="text-sm text-slate-500">{t("investments.period.no_data_in_range")}</p>
      </section>
    );
  }

  // P2P/BETEILIGUNG-Variante unveraendert (TWR macht fuer balance-Kinds keinen Sinn)
  if (kind === "P2P" || kind === "BETEILIGUNG") {
    return (
      <section className="border rounded p-4">
        <header className="mb-3">
          <h2 className="text-lg font-semibold">{t("investments.period.section_title")}</h2>
        </header>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="border rounded p-4">
            <p className="text-xs text-slate-500">{t("investments.period.kpi_dividends_p2p")}</p>
            <p
              className={`text-2xl font-mono font-semibold mt-1 ${colorClass(periodReturn.dividendsNetCents)}`}
            >
              {formatEur(periodReturn.dividendsNetCents)}
            </p>
          </div>
          <div className="border rounded p-4">
            <p className="text-xs text-slate-500">{t("investments.period.kpi_p2p_rendite")}</p>
            <p
              className={`text-2xl font-mono font-semibold mt-1 ${colorClass(periodReturn.totalPercent ?? 0)}`}
            >
              {periodReturn.totalPercent !== null
                ? `${periodReturn.totalPercent.toFixed(2)} %`
                : "—"}
            </p>
          </div>
        </div>
      </section>
    );
  }

  // AKTIE/ETF/KRYPTO/SONSTIGE: TWR als Headline + Erweiterte Ansicht mit Detail-Breakdown
  return (
    <section className="border rounded p-4 space-y-3">
      <header className="mb-1">
        <h2 className="text-lg font-semibold">{t("investments.period.section_title")}</h2>
      </header>

      {/* Headline: TWR */}
      <div>
        <p className="text-xs text-slate-500">{t("investments.period.kpi_twr")}</p>
        <p className={`text-3xl font-mono font-semibold ${colorClass(periodReturn.twrPercent)}`}>
          {periodReturn.twrPercent !== null
            ? `${periodReturn.twrPercent > 0 ? "+" : ""}${periodReturn.twrPercent.toFixed(2)} %`
            : "—"}
        </p>
        {periodReturn.twrPercent === null && (
          <p className="text-xs text-slate-500">{t("investments.period.twr_unavailable")}</p>
        )}
      </div>

      {/* Erweiterte Ansicht: Cashflow-G/V Aufschlüsselung */}
      <button
        type="button"
        className="text-xs text-blue-600 underline"
        onClick={() => setDetailsExpanded(!detailsExpanded)}
      >
        {detailsExpanded ? "▴" : "▾"} {t("investments.period.expandedToggle")}
      </button>

      {detailsExpanded && (
        <div className="border-t pt-3 grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
          <div className="border rounded p-3">
            <p className="text-xs text-slate-500">{t("investments.period.kpi_total")}</p>
            <p
              className={`text-lg font-mono font-semibold mt-1 ${colorClass(periodReturn.totalEur ?? periodReturn.totalCents)}`}
            >
              {/* totalCents in Quote-Currency (z.B. SEK), totalEur ist FX-konvertiert */}
              {formatEur(periodReturn.totalEur ?? periodReturn.totalCents)} (
              {formatPercent(periodReturn.totalPercent)})
            </p>
          </div>
          <div className="border rounded p-3">
            <p className="text-xs text-slate-500">{t("investments.period.kpi_realized")}</p>
            <p className={`text-lg font-mono ${colorClass(periodReturn.realizedCents)}`}>
              {formatEur(periodReturn.realizedCents)}
            </p>
          </div>
          <div className="border rounded p-3">
            <p className="text-xs text-slate-500">{t("investments.period.kpi_unrealized")}</p>
            <p className={`text-lg font-mono ${colorClass(periodReturn.unrealizedDeltaCents)}`}>
              {formatEur(periodReturn.unrealizedDeltaCents)}
            </p>
          </div>
          <div className="border rounded p-3">
            <p className="text-xs text-slate-500">{t("investments.period.kpi_dividends")}</p>
            <p className={`text-lg font-mono ${colorClass(periodReturn.dividendsNetCents)}`}>
              {formatEur(periodReturn.dividendsNetCents)}
            </p>
          </div>
          <div className="border rounded p-3">
            <p className="text-xs text-slate-500">{t("investments.period.kpi_mv_start")}</p>
            <p className="text-lg font-mono text-slate-700">
              {formatEur(periodReturn.mvStartEur ?? periodReturn.mvStartCents)}
            </p>
          </div>
          <div className="border rounded p-3">
            <p className="text-xs text-slate-500">{t("investments.period.kpi_mv_end")}</p>
            <p className="text-lg font-mono text-slate-700">
              {formatEur(periodReturn.mvEndEur ?? periodReturn.mvEndCents)}
            </p>
          </div>
        </div>
      )}
    </section>
  );
}
